/**
 * ClaimSync About Us Page
 * Matching homepage design
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Shield, Award } from 'lucide-react';

export default function AboutPage() {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        header.style.boxShadow = window.pageYOffset > 100 ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628] border-b border-white/10">
        <nav className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex justify-between items-center">
          <Link to="/home" className="flex items-center">
            <img src="/logo.svg" alt="ClaimSync" className="h-44 lg:h-56 w-auto" />
          </Link>
          <ul className="hidden md:flex items-center gap-8 lg:gap-10">
            <li><Link to="/home#features" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Features</Link></li>
            <li><Link to="/about" className="text-white font-medium text-sm tracking-tight">About</Link></li>
            <li><Link to="/contact" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Contact</Link></li>
            <li><Link to="/privacy" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Privacy</Link></li>
            <li>
              <Link 
                to="/sign-in" 
                className="bg-[#2762EA] hover:bg-[#1e50c7] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
              >
                Sign In
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-64 lg:pt-72 pb-16 bg-[#0A1628]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
            About ClaimSync
          </h1>
          <p className="text-xl text-blue-200 leading-relaxed">
            We're on a mission to simplify business interruption claim calculations 
            for accountants, legal professionals, and insurance specialists.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-8 py-20">
        {/* Mission Section */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0A1628] mb-6 tracking-tight">Our Mission</h2>
              <p className="text-lg text-slate-600 mb-4 leading-relaxed">
                ClaimSync was built to solve a real problem: business interruption claims are complex, 
                time-consuming, and require meticulous calculations that leave little room for error.
              </p>
              <p className="text-lg text-slate-600 mb-4 leading-relaxed">
                We created a platform that combines the precision of Standard Business Interruption (BI) 
                methodologies with modern AI capabilities to help professionals generate accurate, 
                defensible claim calculations in a fraction of the time.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our goal is to empower professionals to focus on what matters most: 
                serving their clients and advocating for fair compensation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border border-blue-100/50">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-[#2762EA] mb-2">100+</div>
                  <p className="text-slate-600 text-sm">Industry Templates</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-[#2762EA] mb-2">7</div>
                  <p className="text-slate-600 text-sm">Step Process</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-[#2762EA] mb-2">AI</div>
                  <p className="text-slate-600 text-sm">Powered Analysis</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-[#2762EA] mb-2">24/7</div>
                  <p className="text-slate-600 text-sm">Availability</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-[#0A1628] mb-10 text-center tracking-tight">Our Values</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <ValueCard 
              icon={<Target className="w-6 h-6 text-[#2762EA]" />}
              title="Accuracy"
              description="Precision in every calculation. Our methodologies are built on proven standards."
            />
            <ValueCard 
              icon={<Users className="w-6 h-6 text-[#2762EA]" />}
              title="Collaboration"
              description="Built for teams. Share, review, and collaborate on claims seamlessly."
            />
            <ValueCard 
              icon={<Shield className="w-6 h-6 text-[#2762EA]" />}
              title="Security"
              description="Your data is protected with enterprise-grade security and encryption."
            />
            <ValueCard 
              icon={<Award className="w-6 h-6 text-[#2762EA]" />}
              title="Excellence"
              description="We continuously improve to deliver the best experience possible."
            />
          </div>
        </section>

        {/* What We Do Section */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-[#0A1628] mb-10 text-center tracking-tight">What We Do</h2>
          <div className="bg-[#FAFAF9] rounded-2xl p-10 border border-slate-200/50">
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">BI Calculations</h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Our platform implements the Standard Business Interruption (BI) methodology,
                  providing standardized, defensible calculations for business interruption claims.
                </p>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">AI-Powered Analysis</h3>
                <p className="text-slate-600 leading-relaxed">
                  Using advanced machine learning, we automatically classify transactions, 
                  reconstruct P&L statements, and identify patterns in financial data.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Monte Carlo Simulation</h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Our risk analysis engine runs thousands of simulations to provide claim range 
                  projections with statistical confidence intervals.
                </p>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Professional Reporting</h3>
                <p className="text-slate-600 leading-relaxed">
                  Generate court-ready PDF reports with full audit trails, charts, 
                  supporting calculations, and professional formatting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="bg-[#2762EA] rounded-2xl p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
              Ready to Simplify Your Claims Process?
            </h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              Join professionals who trust ClaimSync for accurate, efficient business interruption claim calculations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/waitlist" 
                className="inline-flex items-center justify-center bg-white text-[#2762EA] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Started
              </Link>
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A1628] py-12">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img src="/logo.svg" alt="ClaimSync" className="h-12 w-auto mb-4" />
              <p className="text-slate-400 text-sm">
                Professional business interruption claim calculations for accountants and legal professionals.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/demo" className="text-slate-400 hover:text-white text-sm transition-colors">Demo</Link></li>
                <li><Link to="/home#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} ClaimSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ValueCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200/50 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
        {icon}
      </div>
      <h3 className="font-bold text-[#0A1628] mb-2">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
